import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useLocation } from 'react-router'

import { useEditor } from '@toasttab/sites-components'

import {
  RankedPromoOfferDiscount,
  RankedPromoOffersResponse,
  useRankedPromoOfferLazyQuery
} from 'src/apollo/onlineOrdering'

import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { alertError } from 'shared/js/alertUtils'

import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import {
  getSegmentedOfferState,
  hasTargetSegment,
  backfillTargetSegmentV2
} from 'public/components/online_ordering/segmentedOfferUtils'

import { useCart } from './CartContext'

export type OfferMap = { [guid: string]: string[] }

export type OffersContextType = {
  itemOffers: OfferMap
  groupOffers: OfferMap
  menuOffers: OfferMap
  getOffersTextForEntity: (
    itemGuid?: string | null,
    groupGuid?: string | null,
    menuGuid?: string | null
  ) => string[]
  rankedPromoOfferDiscounts?: RankedPromoOfferDiscount[]
  achievedOffers: RankedPromoOfferDiscount[]
  loading: boolean
  hasAutoAppliedPromo: boolean
  topOffer: RankedPromoOfferDiscount | undefined
  offerFromUrl: RankedPromoOfferDiscount | null
}

export const OffersContext = createContext<OffersContextType | undefined>(
  undefined
)

const WrappedOffersContextProvider = ({
  paramPromoCode,
  children
}: React.PropsWithChildren<{ paramPromoCode?: string | null }>) => {
  const { ooRestaurant, diningOptions, ooPromoBanners } = useRestaurant()
  const { customer } = useCustomer()
  const {
    applyPromoCode,
    cart,
    cartGuid,
    lastOrderSelectionChange,
    loadingCart
  } = useCart()
  const ooClient = useOOClient()
  const lastOrderSelectionChangeRef = useRef<Date | undefined>()
  const hasAttemptedAutoApply = useRef(false)
  const hasFetchedInitialOffers = useRef(false)
  const [loading, setLoading] = useState(true)

  // Check if there are any promo code banners configured for this restaurant
  const hasPromoCodeBanners =
    (ooPromoBanners?.promoCodeBanners?.length ?? 0) > 0

  // Determine if we should fetch offers at all
  // We need to fetch if there are promo banners OR if there's a promo code in the URL (could be a hidden offer)
  const shouldFetchOffers = hasPromoCodeBanners || !!paramPromoCode
  const [hasAutoAppliedPromo, setHasAutoAppliedPromo] = useState(false)
  const autoAppliedKey = 'hasAutoAppliedPromo'
  const currentAppliedOffer = useMemo(
    () => cart?.order?.discounts?.restaurantDiscount?.promoCode,
    [cart?.order?.discounts?.restaurantDiscount?.promoCode]
  )
  const [topOffer, setTopOffer] = useState<RankedPromoOfferDiscount>()
  const [offerFromUrl, setOfferFromUrl] =
    useState<RankedPromoOfferDiscount | null>()
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const lastAutoAppliedPromo = localStorage.getItem(autoAppliedKey)
      // eslint-disable-next-line no-self-compare
      if (lastAutoAppliedPromo?.length ?? 0 > 0) {
        setHasAutoAppliedPromo(true)
      }
    }
  }, [autoAppliedKey])

  useEffect(() => {
    if (hasAutoAppliedPromo) {
      localStorage.setItem(autoAppliedKey, currentAppliedOffer || '')
    }
  }, [currentAppliedOffer, hasAutoAppliedPromo])

  const clearAutoAppliedPromo = useCallback(() => {
    if (
      currentAppliedOffer &&
      currentAppliedOffer !== localStorage.getItem(autoAppliedKey)
    ) {
      localStorage.removeItem(autoAppliedKey)
      setHasAutoAppliedPromo(false)
    }
  }, [currentAppliedOffer])

  const [getPromoOffers, { data: rankedOffers, loading: loadingPromos }] =
    useRankedPromoOfferLazyQuery({
      fetchPolicy: 'cache-and-network',
      client: ooClient
    })

  // Check if any offers have progress-based requirements that depend on cart contents
  // BOGO offers and offers with minimum spend requirements need to refetch when cart changes
  const hasProgressBasedOffers = useMemo(() => {
    const discounts = rankedOffers?.offers?.rankedPromoOffers
    if (discounts?.__typename !== 'RankedPromoOffersResponse') {
      return false
    }
    return discounts.rankedDiscounts?.some(
      (offer) =>
        offer.discount?.amountType === 'BOGO' ||
        (offer.discount?.minSpendAmount ?? 0) > 0
    )
  }, [rankedOffers])

  const prevIsAuthenticatedRef = useRef<boolean | undefined>(undefined)

  useEffect(() => {
    // Skip fetching if there are no promo banners and no URL promo code
    if (!shouldFetchOffers) {
      setLoading(false)
      return
    }

    const isInitialFetch = !hasFetchedInitialOffers.current
    const cartSelectionChanged =
      lastOrderSelectionChange?.getTime() !==
      lastOrderSelectionChangeRef.current?.getTime()
    const isAuthenticated = !!customer
    const authChanged =
      prevIsAuthenticatedRef.current !== undefined &&
      prevIsAuthenticatedRef.current !== isAuthenticated

    // Only refetch if one of the following is true:
    // 1. This is the initial fetch
    // 2. We have progress-based offers that depend on cart contents
    // 3. The guest's auth state has changed - as some offers are guest-specific
    const shouldRefetch =
      isInitialFetch ||
      (cartSelectionChanged && hasProgressBasedOffers) ||
      authChanged

    if (!loadingCart && ooRestaurant?.guid && shouldRefetch) {
      lastOrderSelectionChangeRef.current = lastOrderSelectionChange
      prevIsAuthenticatedRef.current = isAuthenticated

      hasAttemptedAutoApply.current = false
      clearAutoAppliedPromo()
      getPromoOffers({
        variables: {
          input: {
            restaurantGuid: ooRestaurant?.guid || '',
            requestSource: paramPromoCode
              ? 'online_ordering_including_hidden'
              : 'online_ordering',
            cartGuid: cartGuid
          }
        }
      })
      hasFetchedInitialOffers.current = true
    }
  }, [
    loadingCart,
    cart,
    cartGuid,
    getPromoOffers,
    ooRestaurant?.guid,
    lastOrderSelectionChange,
    clearAutoAppliedPromo,
    paramPromoCode,
    shouldFetchOffers,
    hasProgressBasedOffers,
    customer
  ])

  useEffect(() => {
    if (hasAttemptedAutoApply.current && !loadingPromos) {
      setLoading(false)
      hasAttemptedAutoApply.current = false
    }
  }, [loadingPromos])

  const rankedPromoOffers = rankedOffers?.offers
    .rankedPromoOffers as RankedPromoOffersResponse
  // A null discount field indicates that the offer pertains only to menu items/groups that are not visible in OO, so we filter them out here.
  // We also filter out any hidden offers unless it's the offer from the url
  const rankedPromoOfferDiscounts = useMemo(() => {
    return rankedPromoOffers?.rankedDiscounts
      ?.filter(
        (discount) =>
          discount.discount &&
          (!discount.discount!.displayChannels?.includes(
            'ONLINE_ORDERING_HIDDEN'
          ) ||
            discount.bannerGuid === offerFromUrl?.bannerGuid)
      )
      .map(backfillTargetSegmentV2)
  }, [rankedPromoOffers, offerFromUrl])

  const getTopOffer = useCallback(() => {
    if (offerFromUrl) {
      return rankedPromoOfferDiscounts?.find(
        (offer) => offer.promoCode === offerFromUrl.promoCode
      )
    }
    const currentAppliedOffer =
      cart?.order?.discounts?.restaurantDiscount?.promoCode
    if (currentAppliedOffer) {
      return rankedPromoOfferDiscounts?.find(
        (offer) => offer.promoCode === currentAppliedOffer
      )
    }
    const selectedDiningOptionGuid = diningOptions?.find(
      (option) => option.behavior === cart?.diningOptionBehavior
    )?.guid
    if (rankedPromoOfferDiscounts) {
      for (const offer of rankedPromoOfferDiscounts) {
        // If the top offer is restricted to a dining option that is not selected, skip it
        const restrictions = offer.discount?.triggerDiningOptions
        const doesNotMatchDiningOption = Boolean(
          selectedDiningOptionGuid &&
            restrictions &&
            !restrictions?.includes(selectedDiningOptionGuid)
        )
        // If the top offer is a BOGO and its not >= 75% complete, skip it
        const incompleteBogo =
          offer.discount?.amountType === 'BOGO' && offer.percentCompleteInt < 75

        // Legacy combo offers have trigger entities and their amountType is set to FIXED_TOTAL
        // We filter them out here because they are not supported.
        // Newer offers in the style of "$5 off a soup" have their amountType set to FIXED so they are not filtered.
        const hasTriggerEntities =
          (offer.discount?.triggerMenuItems?.length ?? 0) > 0 ||
          (offer.discount?.triggerMenuGroups?.length ?? 0) > 0 ||
          (offer.discount?.triggerMenus?.length ?? 0) > 0
        const unsupportedLegacyComboOffer =
          hasTriggerEntities && offer.discount?.amountType === 'FIXED_TOTAL'

        const isUnapplicableSegmentedOffer =
          hasTargetSegment(offer) &&
          getSegmentedOfferState(offer, !!customer) !== 'eligible'

        if (
          !doesNotMatchDiningOption &&
          !incompleteBogo &&
          !unsupportedLegacyComboOffer &&
          !isUnapplicableSegmentedOffer
        ) {
          return offer
        }
      }
    }
    return undefined
  }, [
    offerFromUrl,
    cart?.order?.discounts?.restaurantDiscount?.promoCode,
    cart?.diningOptionBehavior,
    diningOptions,
    rankedPromoOfferDiscounts,
    customer
  ])

  useEffect(() => {
    let offer: RankedPromoOfferDiscount | null = null
    if (offerFromUrl === undefined && rankedPromoOffers) {
      if (paramPromoCode) {
        offer =
          rankedPromoOffers.rankedDiscounts.find(
            (discount) => discount.promoCode === paramPromoCode
          ) ?? null
        if (offer === null) {
          alertError(`Promo code ${paramPromoCode} is no longer valid.`)
        } else {
          offer = backfillTargetSegmentV2(offer)
        }
      }
      setOfferFromUrl(offer)
    }
  }, [offerFromUrl, paramPromoCode, rankedPromoOffers])

  useEffect(() => {
    if (offerFromUrl !== undefined) {
      setTopOffer(getTopOffer())
    }
  }, [offerFromUrl, getTopOffer])

  const canAutoApplyTopPromo = useMemo(() => {
    const promoCodes = topOffer?.discount?.promoCodes
    // If the top offer came from the url, we'll auto-apply it even in the case where it's single-use
    const singleUsePromoCode =
      promoCodes &&
      promoCodes.length > 0 &&
      promoCodes[0]?.singleUse &&
      promoCodes[0].code !== offerFromUrl?.promoCode
    const bogoAction = topOffer?.discount?.bogoAction
    const isItemDiscount = topOffer?.discount?.selectionType === 'ITEM'
    const applicableItemDiscount =
      isItemDiscount && !bogoAction && topOffer?.discountEntitiesClaimed
    const isDiscountComplete = topOffer?.percentCompleteInt === 100
    return (
      isDiscountComplete &&
      !currentAppliedOffer &&
      !singleUsePromoCode &&
      (isItemDiscount ? bogoAction || applicableItemDiscount : true) &&
      hasAttemptedAutoApply.current === false
    )
  }, [topOffer, currentAppliedOffer, offerFromUrl?.promoCode])

  const autoApplyTopPromo = useCallback(async () => {
    if (topOffer) {
      const applyError = await applyPromoCode(topOffer.promoCode)
      if (!applyError) {
        setHasAutoAppliedPromo(true)
      }
    }
  }, [applyPromoCode, topOffer])

  useEffect(() => {
    if (canAutoApplyTopPromo) {
      autoApplyTopPromo()
      hasAttemptedAutoApply.current = true
    }
  }, [autoApplyTopPromo, canAutoApplyTopPromo])

  useEffect(() => {
    setLoading(rankedPromoOfferDiscounts === undefined)
  }, [setLoading, rankedPromoOfferDiscounts])

  const getOfferMapForEntity = (
    entityType: 'items' | 'groups' | 'menus',
    offers?: RankedPromoOfferDiscount[]
  ) => {
    const offerMap: OfferMap = {}
    // eslint-disable-next-line array-callback-return
    offers?.map((discount) => {
      const { discount: offer, bannerGuid: offerGuid } = discount
      const triggerEntityKey =
        entityType === 'items'
          ? 'triggerMenuItems'
          : entityType === 'groups'
          ? 'triggerMenuGroups'
          : 'triggerMenus'
      const entities =
        offer?.amountType === 'BOGO'
          ? offer?.bogoAction?.[entityType]
          : offer?.[triggerEntityKey]
      entities?.map(
        (guid) => (offerMap[guid] = [...(offerMap[guid] ?? []), offerGuid])
      )
    })
    return offerMap
  }

  const itemOffers = useMemo(
    () => getOfferMapForEntity('items', rankedPromoOfferDiscounts),
    [rankedPromoOfferDiscounts]
  )
  const groupOffers = useMemo(
    () => getOfferMapForEntity('groups', rankedPromoOfferDiscounts),
    [rankedPromoOfferDiscounts]
  )
  const menuOffers = useMemo(
    () => getOfferMapForEntity('menus', rankedPromoOfferDiscounts),
    [rankedPromoOfferDiscounts]
  )

  const getOffersTextForEntity = useCallback(
    (
      itemGuid?: string | null,
      groupGuid?: string | null,
      menuGuid?: string | null
    ) => {
      const offerGuids = [
        ...(itemGuid ? itemOffers[itemGuid] ?? [] : []),
        ...(groupGuid ? groupOffers[groupGuid] ?? [] : []),
        ...(menuGuid ? menuOffers[menuGuid] ?? [] : [])
      ]
      return (
        rankedPromoOfferDiscounts
          ?.filter((offer) => offerGuids.includes(offer.bannerGuid))
          .map((offer) => offer.bannerText) ?? []
      )
    },
    [itemOffers, groupOffers, menuOffers, rankedPromoOfferDiscounts]
  )

  const achievedOffers = useMemo(
    () =>
      rankedPromoOfferDiscounts?.filter(
        (offer) =>
          offer.percentCompleteInt >= 100 && offer.discountEntitiesClaimed
      ),
    [rankedPromoOfferDiscounts]
  )

  return (
    <OffersContext.Provider
      value={{
        itemOffers,
        groupOffers,
        menuOffers,
        getOffersTextForEntity,
        rankedPromoOfferDiscounts,
        achievedOffers,
        loading,
        hasAutoAppliedPromo,
        topOffer,
        offerFromUrl: offerFromUrl ?? null
      }}
    >
      {children}
    </OffersContext.Provider>
  )
}

const OffersContextProviderWithParams = (
  props: React.PropsWithChildren<{}>
) => {
  const { search } = useLocation()
  const params = useMemo(() => new URLSearchParams(search), [search])

  return (
    <WrappedOffersContextProvider
      {...props}
      paramPromoCode={params.get('promoCode')}
    />
  )
}

const OffersContextProvider = (props: React.PropsWithChildren<{}>) => {
  const { isEditor } = useEditor()

  if (isEditor) {
    return <WrappedOffersContextProvider {...props} />
  } else {
    return <OffersContextProviderWithParams {...props} />
  }
}

export const useOffers = () => {
  const context = useContext(OffersContext)
  if (!context) {
    throw new Error('useOffers must be used within an OffersContextProvider')
  }

  return context
}

export default OffersContextProvider
